// src/whatsappChat/components/ChatTemplateBar.jsx
import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useWaCustomTemplates } from '../api/hooks';
import { normalizeError } from '../api/client';

/**
 * Chat template picker shown above the message box, in the same bar as the
 * suggested replies. Templates are created and edited on the Setup page.
 */
export function ChatTemplateBar({ activeText, onPick, onClose }) {
  const { data: templates = [], isLoading, isError, error } = useWaCustomTemplates();
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const usable = templates.filter((t) => t.template_body);
    const q = search.trim().toLowerCase();
    return q
      ? usable.filter(
          (t) =>
            String(t.template_name || '').toLowerCase().includes(q) ||
            String(t.template_body).toLowerCase().includes(q),
        )
      : usable;
  }, [templates, search]);

  const onSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    } else if (e.key === 'Enter' && visible.length > 0) {
      e.preventDefault();
      onPick(visible[0]);
    }
  };

  return (
    <div className="wa-ai-bar wa-ct-bar">
      <div className="wa-ai-bar-header">
        <span className="wa-ai-title">
          <FontAwesomeIcon icon={faBookmark} className="wa-ai-title-icon" />
          Chat Templates
        </span>
        <div className="wa-ai-bar-actions">
          <button type="button" className="wa-ai-icon-btn" onClick={onClose} title="Close" aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>

      <label className="wa-search wa-ct-search">
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder="Search chat templates"
          aria-label="Search chat templates"
          autoFocus
        />
      </label>

      {isLoading ? (
        <div className="wa-ct-empty">Loading templates…</div>
      ) : isError ? (
        <div className="wa-ct-empty">{normalizeError(error).message}</div>
      ) : visible.length === 0 ? (
        <div className="wa-ct-empty">
          {search.trim() ? 'No templates match.' : 'No chat templates yet. Add them from Setup.'}
        </div>
      ) : (
        <div className="wa-ct-list">
          {visible.map((tpl) => {
            const isSelected = activeText === tpl.template_body;
            return (
              <button
                key={tpl.id}
                type="button"
                className={`wa-ai-chip wa-ct-item${isSelected ? ' is-selected' : ''}`}
                onClick={() => onPick(tpl)}
                title={tpl.template_body}
              >
                <span className="wa-ct-item-body">{tpl.template_body}</span>
                <span className="wa-ai-chip-action">{isSelected ? 'Selected ✓' : 'Use ↵'}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
